import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Loader } from '@/components/Loader';

/**
 * Multi-step form wizard with visual step indicator.
 *
 * Props:
 *  - steps: [{ key, title, icon: LucideIcon, render: (form, set, errors) => ReactNode, validate?: (form) => string[] }]
 *  - initialForm: object
 *  - onSubmit: async (form) => void   (called at last step; must throw to keep wizard open)
 *  - submitLabel: string (default t('common.confirm'))
 *  - testidPrefix: string
 *  - loading: boolean (external submit state → shows overlay + disables buttons)
 *
 * The wizard is a controlled multi-step form; it does not render its own Dialog
 * so parents can wrap it in <Dialog> as needed.
 */
export function Wizard({
  steps,
  initialForm,
  onSubmit,
  submitLabel,
  testidPrefix = 'wizard',
  loading = false,
}) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    // reset when initialForm identity changes (parent reopened)
    setForm(initialForm);
    setCurrent(0);
    setErrors([]);
  }, [initialForm]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const step = steps[current];
  const isLast = current === steps.length - 1;

  const goNext = async () => {
    setErrors([]);
    const errs = step.validate ? step.validate(form) : [];
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    if (isLast) {
      try {
        await onSubmit(form);
      } catch {
        // parent handled toast
      }
    } else {
      setCurrent((c) => c + 1);
    }
  };

  const goPrev = () => {
    setErrors([]);
    setCurrent((c) => Math.max(0, c - 1));
  };

  const progress = useMemo(
    () => Math.round(((current + 1) / steps.length) * 100),
    [current, steps.length]
  );

  return (
    <div data-testid={testidPrefix} className="relative">
      {loading && (
        <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-10 rounded-lg flex items-center justify-center">
          <Loader variant="full" />
        </div>
      )}

      {/* Step indicator */}
      <ol className="mb-5 flex min-w-0 items-start gap-0.5 sm:mb-6 sm:gap-2" aria-label="Étapes">
        {steps.map((s, i) => {
          const done = i < current;
          const active = i === current;
          const Icon = s.icon;
          return (
            <li key={s.key} className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2">
              <div className="flex flex-col items-center gap-1.5 w-full">
                <div
                  data-testid={`${testidPrefix}-step-${i}`}
                  className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all sm:h-9 sm:w-9
                    ${done ? 'bg-[#22C55E] text-white' : active ? 'bg-[#1677FF] text-white ring-4 ring-[#E6F1FF]' : 'bg-slate-100 text-slate-400'}`}
                >
                  {done ? <Check className="w-4 h-4" /> : Icon ? <Icon className="w-4 h-4" /> : <span className="text-xs font-bold">{i + 1}</span>}
                </div>
                <div className={`max-w-full break-words text-center text-[8px] font-semibold uppercase leading-tight tracking-tight sm:text-xs sm:tracking-wide ${active ? 'text-[#0D1B2A]' : 'text-slate-400'}`}>
                  {s.title}
                </div>
              </div>
              {i < steps.length - 1 && (
                <div className={`hidden sm:block h-[2px] flex-1 -mt-6 rounded ${i < current ? 'bg-[#22C55E]' : 'bg-slate-200'}`} />
              )}
            </li>
          );
        })}
      </ol>

      {/* Progress (mobile) */}
      <div className="sm:hidden mb-4">
        <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
          <span>{t('common.step')} {current + 1} {t('common.of')} {steps.length}</span>
          <span>{progress}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-[#1677FF] transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Errors bar */}
      {errors.length > 0 && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
          <ul className="text-xs text-red-700 list-disc list-inside space-y-0.5">
            {errors.map((e) => <li key={e}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* Step content */}
      <div data-testid={`${testidPrefix}-content-${current}`} className="min-h-[220px] min-w-0 sm:min-h-[260px]">
        {step.render(form, set, errors)}
      </div>

      {/* Nav buttons */}
      <div className="mt-6 flex flex-col-reverse gap-2 min-[420px]:flex-row min-[420px]:items-center min-[420px]:justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={goPrev}
          disabled={current === 0 || loading}
          className="w-full min-[420px]:w-auto"
          data-testid={`${testidPrefix}-prev`}
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> {t('common.back')}
        </Button>
        <Button
          type="button"
          onClick={goNext}
          disabled={loading}
          className="w-full bg-[#1677FF] hover:bg-[#0E62DB] min-[420px]:w-auto"
          data-testid={`${testidPrefix}-next`}
        >
          {isLast
            ? (loading ? t('common.processing') : (submitLabel || t('common.confirm')))
            : (<>{t('common.next')} <ChevronRight className="w-4 h-4 ml-1" /></>)
          }
        </Button>
      </div>
    </div>
  );
}
